/**
 * Real-user Web Vitals (web plan W5.2): LCP, INP, CLS and TTFB measured with
 * Google's `web-vitals` library (the way Chrome, CrUX and Search Console
 * measure them) and sent to GA4 through `@/lib/analytics` (`trackWebVital`),
 * grouped by page template (`@/lib/pageTemplate`).
 *
 * Only where analytics reports at all (`canReport`: a production build, in a
 * browser, used by a person): crawlers, Lighthouse and automated browsers
 * don't even download the library, which is a separate ~3 kB chunk fetched
 * after the app has started, off the critical path. Its observers read the
 * buffered performance entries, so nothing from before it loads is missed.
 *
 * Metrics belong to the page LOAD, not to later in-app navigations (the
 * library's default, like CrUX): the template is the one of the URL the page
 * was loaded at (`navigationURL`), even when CLS and INP are only sent when
 * the tab is hidden, possibly pages later.
 */
import type { MetricType } from 'web-vitals';
import { canReport, trackWebVital } from '@/lib/analytics';
import { pageTemplate } from '@/lib/pageTemplate';

type WebVitalsModule = Pick<typeof import('web-vitals'), 'onCLS' | 'onINP' | 'onLCP' | 'onTTFB'>;

const loadWebVitals = (): Promise<WebVitalsModule> => import('web-vitals');

/**
 * Start measuring. Resolves once the observers are registered (or at once
 * when nothing is reported); never rejects. `load` is for tests.
 */
export function startWebVitals(load: () => Promise<WebVitalsModule> = loadWebVitals): Promise<void> {
  if (!canReport()) return Promise.resolve();
  // The address this page was loaded at, for a browser whose navigation
  // entry is missing (the library then has no navigationURL).
  const landingUrl = window.location.href;
  return load()
    .then(({ onCLS, onINP, onLCP, onTTFB }) => {
      const report = (metric: MetricType) => {
        if (metric.name === 'FCP') return;
        trackWebVital(metric, pageTemplate(metric.navigationURL ?? landingUrl));
      };
      onLCP(report);
      onINP(report);
      onCLS(report);
      onTTFB(report);
    })
    .catch(() => {
      /* measuring is best-effort: a failed chunk load changes nothing for the visitor */
    });
}
